/**
 * LeendrLogoMark — animated SVG version of the brand mark.
 *
 * Concept: a stylised lowercase "l" centred between two rotating arc-arrows
 * that suggest the lend-and-return cycle. Arrowheads at the tail of each arc
 * make the rotation direction (and the "exchange" idea) immediately obvious.
 *
 * Pure SVG + CSS animation, no JS, no image fetch — instant render, scalable
 * to any pixel size, perfect for the sticky header.
 */
import './LeendrLogoMark.css';

export default function LeendrLogoMark({ size = 34, className = '', arrowScale = 1 }) {
  // Each instance needs its own marker id so multiple logos on the same page
  // don't collide when the arrowhead is rendered at different sizes.
  const markerId = `lm-arrowhead-${arrowScale.toString().replace('.', '_')}`;
  const m = 4.5 * arrowScale;
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 64 64"
      width={size}
      height={size}
      className={`leendr-logo-mark ${className}`}
      aria-label="leendr"
      role="img"
    >
      {/* Marker definition: a clearly visible filled triangle that auto-orients
          to the path's tangent direction at the arc tail. */}
      <defs>
        <marker
          id={markerId}
          viewBox="0 0 10 10"
          markerWidth={m}
          markerHeight={m}
          refX="2.5"
          refY="5"
          orient="auto"
          markerUnits="strokeWidth"
        >
          <path d="M 0 0 L 10 5 L 0 10 Z" fill="#534AB7" />
        </marker>
      </defs>

      {/* Soft rounded square background — matches the brand surface */}
      <rect x="0" y="0" width="64" height="64" rx="14" fill="#EEEDFE" />

      {/* Outer rotating ring of two arc-arrows (the "lend / return" loop).
          Arcs sit on a wider radius (~22) to give the central "l" a clean
          breathing zone — they no longer touch the wordmark seed. */}
      <g className="lm-arrows" style={{ transformOrigin: '32px 32px' }}>
        {/* Right arc — clockwise from top-right to bottom-right */}
        <path
          d="M 51 21 A 22 22 0 0 1 51 43"
          fill="none"
          stroke="#534AB7"
          strokeWidth="3.6"
          strokeLinecap="round"
          markerEnd={`url(#${markerId})`}
        />
        {/* Left arc — clockwise from bottom-left to top-left (continues the loop) */}
        <path
          d="M 13 43 A 22 22 0 0 1 13 21"
          fill="none"
          stroke="#534AB7"
          strokeWidth="3.6"
          strokeLinecap="round"
          markerEnd={`url(#${markerId})`}
        />
      </g>

      {/* Centred lowercase "l" — the wordmark seed. Slightly trimmed so it sits
          fully inside the rotating ring with a comfortable gap. */}
      <path
        d="M 31 19 L 31 41 Q 31 45 35 45"
        fill="none"
        stroke="#534AB7"
        strokeWidth="4.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
